import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ProjectAPI } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Skeleton } from '../components/ui/Skeleton';
import { TopBar } from '../components/TopBar';
import { useToast } from '../components/ui/Toast';
import { formatRelative } from '../lib/format';
import { ArrowRight } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: '', label: 'Всі' },
  { value: 'active', label: 'Активні' },
  { value: 'paused', label: 'Призупинені' },
  { value: 'archived', label: 'Архівовані' },
];

const statusVariant: Record<string, 'success' | 'warning' | 'muted'> = {
  active: 'success',
  paused: 'warning',
  archived: 'muted',
};

export function ProjectsPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const { toast } = useToast();
  const qc = useQueryClient();

  const params: Record<string, string> = {};
  if (statusFilter) params.status = statusFilter;

  const { data: projectsData, isLoading } = useQuery({
    queryKey: ['projects', params],
    queryFn: () => ProjectAPI.list(params),
  });

  const importMut = useMutation({
    mutationFn: ProjectAPI.importFromFile,
    onSuccess: () => {
      toast('Проєкти імпортовано', 'success');
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const projects: any[] = projectsData?.items ?? projectsData ?? [];

  return (
    <div>
      <TopBar
        title="Проєкти"
        actions={
          <Button
            variant="outline"
            size="sm"
            loading={importMut.isPending}
            onClick={() => importMut.mutate()}
          >
            Імпортувати з PROJECTS.md
          </Button>
        }
      />
      <div className="p-6">
        <div className="mb-4">
          <Select
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-44"
          />
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-40 w-full" />)}
          </div>
        ) : projects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project: any) => (
              <Card
                key={project.slug || project.id}
                className="cursor-pointer hover:border-tg-accent transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{
                        background: project.status === 'active'
                          ? 'var(--tg-success)'
                          : project.status === 'paused'
                          ? 'var(--tg-warning)'
                          : 'var(--tg-text-secondary)'
                      }}
                    />
                    <h3 className="font-semibold text-tg-text">{project.name}</h3>
                  </div>
                  <Badge variant={statusVariant[project.status] ?? 'muted'}>
                    {project.status || '—'}
                  </Badge>
                </div>

                {project.description && (
                  <p className="text-sm text-tg-text-secondary truncate-3 mb-3">
                    {project.description}
                  </p>
                )}

                <div className="flex flex-wrap gap-1 mb-3">
                  {project.serviceType && <Badge variant="muted">{project.serviceType}</Badge>}
                  {project.language && <Badge variant="muted">{project.language}</Badge>}
                  {project.port && <Badge variant="info">:{project.port}</Badge>}
                </div>

                <div className="flex items-center justify-between text-xs text-tg-text-secondary">
                  <span>{project.tasksCount ?? 0} задач</span>
                  {project.lastRunAt && <span>{formatRelative(project.lastRunAt)}</span>}
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 w-full justify-between"
                  onClick={() => navigate({ to: '/projects/$slug', params: { slug: project.slug || project.id } })}
                >
                  Деталі <ArrowRight size={14} />
                </Button>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-tg-text-secondary">
            Проєкти не знайдені
          </div>
        )}
      </div>
    </div>
  );
}
